/**
 * Currency detection and formatting utilities for regional pricing
 * Shared between marketing site and web app
 */

export type CurrencyCode = 'USD' | 'GBP' | 'EUR';

interface CurrencyConfig {
  symbol: string;
  locale: string;
}

export const CURRENCIES: Record<CurrencyCode, CurrencyConfig> = {
  USD: { symbol: '$', locale: 'en-US' },
  GBP: { symbol: '£', locale: 'en-GB' },
  EUR: { symbol: '€', locale: 'de-DE' },
};

/**
 * Regional prices in smallest unit (cents/pence)
 */
export const REGIONAL_PRICES = {
  PRO: {
    USD: { monthly: 600, yearly: 6000 },   // $6/month, $60/year
    GBP: { monthly: 600, yearly: 6000 },   // £6/month, £60/year
    EUR: { monthly: 600, yearly: 6000 },   // €6/month, €60/year
  },
} as const;

/**
 * EU language codes that should default to EUR
 */
export const EU_LOCALES = [
  'de', // German
  'fr', // French
  'es', // Spanish
  'it', // Italian
  'nl', // Dutch
  'pt', // Portuguese
  'pl', // Polish
  'sv', // Swedish
  'da', // Danish
  'fi', // Finnish
  'el', // Greek
  'cs', // Czech
  'ro', // Romanian
  'hu', // Hungarian
  'sk', // Slovak
  'bg', // Bulgarian
  'hr', // Croatian
  'sl', // Slovenian
  'et', // Estonian
  'lv', // Latvian
  'lt', // Lithuanian
  'mt', // Maltese
  'ga', // Irish
] as const;

/**
 * Detect currency from browser locale
 * @param locale - Browser locale string (e.g., navigator.language)
 * @returns Currency code based on locale
 */
export function detectCurrencyFromLocale(locale: string): CurrencyCode {
  const lowerLocale = locale.toLowerCase();

  // Check for UK locale
  if (lowerLocale === 'en-gb') {
    return 'GBP';
  }

  // Check for Ireland (uses EUR)
  if (lowerLocale === 'en-ie') {
    return 'EUR';
  }

  // Check for EU locales
  const languageCode = lowerLocale.split('-')[0] || '';
  if ((EU_LOCALES as readonly string[]).includes(languageCode)) {
    return 'EUR';
  }

  // Default to USD
  return 'USD';
}

/**
 * Detect currency from browser (client-side only)
 * @returns Currency code based on navigator.language
 */
export function detectCurrency(): CurrencyCode {
  if (typeof navigator === 'undefined') {
    return 'USD';
  }
  return detectCurrencyFromLocale(navigator.language || 'en-US');
}

/**
 * Get locale for a currency code (for Intl formatting)
 */
export function getLocaleForCurrency(currency: CurrencyCode): string {
  return CURRENCIES[currency].locale;
}

/**
 * Format price from cents to display string
 * @param cents - Price in smallest unit (cents/pence)
 * @param currency - Currency code
 * @returns Formatted price string
 */
export function formatPrice(cents: number, currency: CurrencyCode): string {
  const config = CURRENCIES[currency];
  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

/**
 * Get regional price for a plan
 * @param plan - Plan ID (currently only 'PRO')
 * @param currency - Currency code
 * @param interval - 'monthly' or 'yearly'
 * @returns Price in cents
 */
export function getRegionalPrice(
  plan: keyof typeof REGIONAL_PRICES,
  currency: CurrencyCode,
  interval: 'monthly' | 'yearly'
): number {
  return REGIONAL_PRICES[plan][currency][interval];
}

/**
 * Get formatted regional prices for display
 * @param plan - Plan ID (currently only 'PRO')
 * @param currency - Currency code
 * @returns Object with formatted monthly and yearly prices
 */
export function getFormattedPrices(
  plan: keyof typeof REGIONAL_PRICES,
  currency: CurrencyCode
): { monthly: string; yearly: string; yearlyPerMonth: string } {
  const prices = REGIONAL_PRICES[plan][currency];
  return {
    monthly: formatPrice(prices.monthly, currency),
    yearly: formatPrice(prices.yearly, currency),
    yearlyPerMonth: formatPrice(Math.round(prices.yearly / 12), currency),
  };
}
